import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { assignUser as assignUserAPI } from 'api/documents'
import { User } from 'api/user'
import DialogBase from 'components/dialog/Base'
import LoadingWrapper from 'components/LoadingWrapper'
import Field from 'components/ui/Field'
import { DocListItem } from 'entities/Documents'
import useUserSubGroup from 'hooks/useUserSubGroup'
import { useEffect, useState, useMemo } from 'react'

type Props = {
  open: boolean
  setOpenDialog: (x: boolean) => void
  documents: DocListItem[]
  objectIDs: string[]
  reloadDocs: () => void
}

const AssignUserDialog = ({ open, setOpenDialog, documents, objectIDs, reloadDocs }: Props) => {
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const selectedDocs = documents.filter((doc) => objectIDs.includes(doc.objectID))
  const reqGroups = useMemo(
    () => new Set(selectedDocs.map((doc) => doc.subGroup).filter(Boolean)),
    [selectedDocs],
  )
  const { users, subGroups, setReqGroupNames, ready } = useUserSubGroup()

  useEffect(() => {
    setReqGroupNames(reqGroups)
  }, [reqGroups, setReqGroupNames])

  const assignableUsers = useMemo(() => {
    if (reqGroups.size > 0) {
      const assignableIds = Object.entries(subGroups)
        .filter(([k, _v]) => reqGroups.has(k))
        .map(([_k, v]) => v)
        .flat()
      const assignableIdsSet = new Set(assignableIds)
      return users.filter((u) => assignableIdsSet.has(u.userID))
    }
    return users
  }, [reqGroups, subGroups, users])

  useEffect(() => {
    if (!user) return
    if (!assignableUsers.find((u) => u.userID === user.userID)) {
      setUser(null)
    }
  }, [assignableUsers, user])

  const submit = async () => {
    setLoading(true)

    try {
      await assignUserAPI({
        objectIDs: objectIDs,
        assigneeUserID: user!.userID,
      })
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
    setOpenDialog(false)
    reloadDocs()
  }

  const close = () => {
    setOpenDialog(false)
  }

  return (
    <DialogBase
      open={open}
      loading={loading}
      title="担当者を設定"
      onCancel={close}
      onClose={close}
      onAction={submit}
      actionProps={{ disabled: !user }}
      actionText="保存"
      cancelText="キャンセル"
    >
      <LoadingWrapper loaded={ready}>
        <Field inline label="ユーザーを選択">
          <Autocomplete
            value={user}
            onChange={(_event, newValue) => {
              setUser(newValue)
            }}
            getOptionLabel={(option) => option.userName}
            options={assignableUsers}
            sx={{ width: 280 }}
            renderInput={(params) => (
              <TextField
                margin="dense"
                variant="outlined"
                placeholder="グループ内のユーザーを選択"
                {...params}
              />
            )}
            data-testid="select-assign-user"
          />
        </Field>
      </LoadingWrapper>
    </DialogBase>
  )
}

export default AssignUserDialog
